import type { DisciplineScheduleFlow, DisciplineScheduleStep } from '../../enums'
import type { DisciplineScheduleStore } from './interfaces'

import { DisciplineScheduleEffectiveMode } from '../../enums'
import { SCHEDULE_FLOW_STEPS, getScheduleStepSibling } from '../../discipline-schedule'
import { createDisciplineScheduleState } from './state'

export const actions = {
  /** Every wizard starts from a clean state, as legacy cleared its store on every close. */
  openFlow (this: DisciplineScheduleStore, flow: DisciplineScheduleFlow): void {
    Object.assign(this, createDisciplineScheduleState())
    this.flow = flow
    this.step = SCHEDULE_FLOW_STEPS[flow][0] ?? null
  },
  closeFlow (this: DisciplineScheduleStore): void {
    Object.assign(this, createDisciplineScheduleState())
  },
  setStep (this: DisciplineScheduleStore, payload: DisciplineScheduleStep): void {
    this.step = payload
  },
  nextStep (this: DisciplineScheduleStore): void {
    if (!this.flow || !this.step) return
    this.step = getScheduleStepSibling(this.flow, this.step, 1) ?? this.step
  },
  previousStep (this: DisciplineScheduleStore): void {
    if (!this.flow || !this.step) return
    this.step = getScheduleStepSibling(this.flow, this.step, -1) ?? this.step
  },
  setGrid (this: DisciplineScheduleStore, payload: DisciplineScheduleStore['grid']): void {
    this.grid = payload
  },
  setGridLoading (this: DisciplineScheduleStore, payload: boolean): void {
    this.gridLoading = payload
  },
  setWeeksCount (this: DisciplineScheduleStore, payload: number): void {
    this.weeksCount = payload
  },
  setSelected (this: DisciplineScheduleStore, payload: DisciplineScheduleStore['selected']): void {
    this.selected = payload
  },
  setSlotsError (this: DisciplineScheduleStore, payload: boolean): void {
    this.slotsError = payload
  },
  setStartDate (this: DisciplineScheduleStore, payload: string): void {
    this.startDate = payload
  },
  setEffectiveMode (this: DisciplineScheduleStore, payload: DisciplineScheduleEffectiveMode): void {
    this.effectiveMode = payload
    if (payload === DisciplineScheduleEffectiveMode.Now) this.effectiveDate = null
  },
  setEffectiveDate (this: DisciplineScheduleStore, payload: string | null): void {
    this.effectiveDate = payload
  },
  setPace (this: DisciplineScheduleStore, payload: number | null): void {
    this.pace = payload
  },
  patchFilters (this: DisciplineScheduleStore, payload: Partial<DisciplineScheduleStore['filters']>): void {
    this.filters = { ...this.filters, ...payload }
  },
  setActivationRunId (this: DisciplineScheduleStore, payload: number | null): void {
    this.activationRunId = payload
  },
  addDoneRunId (this: DisciplineScheduleStore, payload: number): void {
    if (!this.doneRunIds.includes(payload)) this.doneRunIds = [...this.doneRunIds, payload]
  },
  setAllocating (this: DisciplineScheduleStore, payload: boolean): void {
    this.allocating = payload
  },
  setAllocationResults (
    this: DisciplineScheduleStore,
    payload: Pick<DisciplineScheduleStore, 'allocationLogs' | 'allocationTutors'>,
  ): void {
    this.allocationLogs = payload.allocationLogs
    this.allocationTutors = payload.allocationTutors
  },
  /** Back from the tutor list drops the run: Continue on the filters starts a new one. */
  resetAllocation (this: DisciplineScheduleStore): void {
    this.activationRunId = null
    this.allocationLogs = null
    this.allocationTutors = []
    this.chosenLog = null
  },
  setChosenLog (this: DisciplineScheduleStore, payload: DisciplineScheduleStore['chosenLog']): void {
    this.chosenLog = payload
  },
  setTutorReason (this: DisciplineScheduleStore, payload: DisciplineScheduleStore['tutorReason']): void {
    this.tutorReason = payload
    this.tutorSubReasons = []
    this.reasonError = !payload
  },
  setTutorSubReasons (this: DisciplineScheduleStore, payload: string[]): void {
    this.tutorSubReasons = payload
  },
  setTutorComment (this: DisciplineScheduleStore, payload: string): void {
    this.tutorComment = payload
  },
  setReasonError (this: DisciplineScheduleStore, payload: boolean): void {
    this.reasonError = payload
  },
  setConfirmed (this: DisciplineScheduleStore, payload: boolean): void {
    this.confirmed = payload
    this.confirmError = !payload
  },
  setConfirmError (this: DisciplineScheduleStore, payload: boolean): void {
    this.confirmError = payload
  },
  setSaving (this: DisciplineScheduleStore, payload: boolean): void {
    this.saving = payload
  },
}
